import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import SingleSelectStep from "../steps/SingleSelectStep";
import MultiSelectStep from "../steps/MultiSelectStep";
import NumberInputStep from "../steps/NumberInputStep";
import TabbedImageSelectStep from "../steps/TabbedImageSelectStep";
import PopupChoiceStep from "../steps/PopupChoiceStep";
import { saveQuickQuiz } from "../../../features/quickQuiz/quickQuizSlice";
import { deriveLensRecommendations } from "../flows/spectacleLensFlow";

const QuizEngine = ({ flow, categoryId, masterData, onFilterChange, prevToCategoryPicker }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [subChoice, setSubChoice] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const step = flow.steps[stepIndex];

  const finish = (finalAnswers) => {
    dispatch(saveQuickQuiz({ p_category_id: categoryId, quiz_data: finalAnswers }));

    if (flow.onComplete === "showPopup") {
      setShowPopup(true);
    } else {
      navigate("/all-products"); 
    }
  };

  const handleAnswer = (value) => {
    const updated = { ...answers, [step.id]: value };

    if (step.filterKey && value && onFilterChange) {
      onFilterChange(step.filterKey, value);
    }

    if (step.id === "deviceUsed") {
      if (value?.name === "Mobile Phone") {
        updated.recommendations = deriveLensRecommendations(updated);
        setAnswers(updated);
        setStepIndex((i) => i + 1);
        return;
      }
      if (value?.name === "Laptop" || value?.name === "Computer/Desktop") {
        setSubChoice({ options: [{ id: 1, name: "Progressive" }, { id: 2, name: "Office Lens Plus" }] });
        setAnswers(updated);
        return;
      }
    }

    setAnswers(updated);

    const isLastStep = stepIndex === flow.steps.length - 1;
    if (isLastStep) {
      const finalAnswers =
        step.id === "timeOutside"
          ? { ...updated, recommendations: deriveLensRecommendations(updated) }
          : updated;
      finish(finalAnswers);
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const handleSubChoiceAnswer = (chosenLensType) => {
    const updated = { ...answers, deviceLensChoice: chosenLensType, recommendations: [chosenLensType.name] };
    setSubChoice(null);
    setAnswers(updated);
    setStepIndex((i) => i + 1);
  };

  if (showPopup) {
    return <PopupChoiceStep onClose={() => setShowPopup(false)} />;
  }

  if (subChoice) {
    return (
      <SingleSelectStep
        step={{ question: "Which lens type would you prefer?", variant: "text" }}
        options={subChoice.options}
        stepLabel="4/06"
        onAnswer={handleSubChoiceAnswer}
      />
    );
  }

  if (!step) return null;

  const prevStep = stepIndex > 0 ? () => setStepIndex((i) => i - 1) : prevToCategoryPicker;

  switch (step.type) {
    case "number":
      return <NumberInputStep step={step} stepLabel={step.stepLabel} prevStep={prevStep} onAnswer={handleAnswer} />;
    case "multi":
      return <MultiSelectStep step={step} stepLabel={step.stepLabel} prevStep={prevStep} onAnswer={handleAnswer} />;
    case "tabbed":
      return (
        <TabbedImageSelectStep
          step={step}
          tabs={step.tabs}
          productsByTab={masterData?.lensProductsByDisposability}
          stepLabel={step.stepLabel}
          prevStep={prevStep}
          onAnswer={handleAnswer}
        />
      );
    case "single":
    default:
      return (
        <SingleSelectStep
          step={step}
          options={step.staticOptions || masterData?.[step.dataKey]}
          stepLabel={step.stepLabel}
          prevStep={prevStep}
          onAnswer={handleAnswer}
        />
      );
  }
};

export default QuizEngine;